import React, { useState, useEffect, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  Home, TrendingUp, Newspaper, Database, Mail, Briefcase,
  Eye, LineChart, Activity, PlusCircle, Search, BarChart3,
  GitCompare, ChevronLeft, ChevronRight, ChevronDown, X, Loader2,
} from "lucide-react";
import AutoGraphIcon from "@mui/icons-material/AutoGraph";
import LeaderboardIcon from "@mui/icons-material/Leaderboard";
import InsightsIcon from "@mui/icons-material/Insights";
import FunctionsIcon from "@mui/icons-material/Functions";
import ScienceIcon from "@mui/icons-material/Science";
import { Typography, Tooltip } from "@mui/material";
import { getAllPortfolios } from "../../api/portfolio_api";

/* ── tiny helper: wraps children in a right-side tooltip only when collapsed ── */
const SideTooltip = ({ label, collapsed, children }) =>
  collapsed ? (
    <Tooltip title={label} placement="right" arrow>
      {children}
    </Tooltip>
  ) : (
    children
  );

const market = sessionStorage.getItem("selectedMarket") ?? "US";
const marketIsAll = market === "ALL";
const Sidebar = ({ isCollapsed, setIsCollapsed, isMobileOpen, setIsMobileOpen }) => {
  const location = useLocation();
  const navigate = useNavigate();

  const [openMenu, setOpenMenu] = useState(null);
  const [portfolios, setPortfolios] = useState([]);
  const [portfoliosLoading, setPortfoliosLoading] = useState(false);
  const [portfoliosFetched, setPortfoliosFetched] = useState(false);

  const isActive = (path) => location.pathname === path;
  const isNewsletterActive = () => location.pathname === "/settings/newsletters";

  const isOnPortfolioDetail = location.pathname.startsWith("/portfolio/");
  const activePortfolioId = isOnPortfolioDetail
    ? location.pathname.split("/portfolio/")[1]?.split("/")[0]
    : null;

  const fetchPortfolios = useCallback(async (force = false) => {
    if (portfoliosFetched && !force) return;
    setPortfoliosLoading(true);
    const result = await getAllPortfolios();
    if (result.success) {
      setPortfolios(result.data);
      setPortfoliosFetched(true);
    }
    if (result.authExpired) navigate("/login");
    setPortfoliosLoading(false);
  }, [portfoliosFetched, navigate]);

  useEffect(() => {
    if (isOnPortfolioDetail) {
      setOpenMenu("All Portfolios");
      fetchPortfolios();
    }
  }, [isOnPortfolioDetail]);

  useEffect(() => {
    if (location.pathname === "/allPortfolio" || isOnPortfolioDetail) {
      if (portfoliosFetched) fetchPortfolios(true);
    }
  }, [location.pathname]);

  const handlePortfolioToggle = () => {
    const isOpen = openMenu === "All Portfolios";
    if (!isOpen) fetchPortfolios(true);
    setOpenMenu(isOpen ? null : "All Portfolios");
  };

  const handleLinkClick = () => { };

  const menuSections = [
    {
      items: [
        { name: "Home", path: "/dashboard", icon: Home },
        { name: "News", path: "/news", icon: Newspaper },
        { name: "Market Data", path: "/marketData", icon: Database },
        { name: "Newsletters", path: "/settings/newsletters", icon: Mail },
      ],
    },
    {
      title: "Portfolio",
      items: [
        { name: "All Portfolios", path: "/allPortfolio", icon: Briefcase, expandable: true },
        { name: "Watchlist", path: "/watchlist", icon: LineChart },
        { name: "Recently Viewed", path: "/recentlyViewed", icon: Eye },
        ...(market !== "ALL" ? [{ name: "Create Portfolio", action: "createPortfolio", icon: PlusCircle }] : []),
      ],
    },
    {
      title: "Find & Compare",
      items: [
        { name: "FA Factor Rating", path: "/quantRating", icon: InsightsIcon },
        { name: "Efficient Frontier Simulator", path: "/frontierSimulator", icon: FunctionsIcon },
        {
          name: "Screener",
          icon: Search,
          subMenu: [
            { name: "By FA", path: "/stocksScreenerbyQuants" },
            { name: "By Ratios", path: "/stocksScreenerbyRatios" },
          ],
        },
        { name: "Comparisons", path: "/comparison", icon: GitCompare },
      ],
    },
  ];

  /* ── shared item style helpers ── */
  const itemBase = {
    display: "flex",
    alignItems: "center",
    gap: "var(--spacing-md)",
    padding: "10px var(--spacing-md)",
    borderRadius: "var(--radius-md)",
    transition: "all var(--transition-fast)",
    cursor: "pointer",
    width: "100%",
    textDecoration: "none",
    fontFamily: "inherit",
    background: "transparent",
    border: "none",
    borderLeft: "4px solid transparent",
    boxSizing: "border-box",
  };

  const itemActive = {
    backgroundColor: "var(--color-surface-hover)",
    color: "var(--color-text-primary)",
    borderLeft: "4px solid var(--color-primary)",
  };

  const itemInactive = {
    color: "var(--color-text-secondary)",
  };

  const onHoverIn = (e) => {
    e.currentTarget.style.color = "var(--color-text-primary)";
    e.currentTarget.style.backgroundColor = "var(--color-surface-hover)";
  };

  const onHoverOut = (e, active) => {
    if (!active) {
      e.currentTarget.style.color = "var(--color-text-secondary)";
      e.currentTarget.style.backgroundColor = "transparent";
    }
  };

  return (
    <>
      {/* Mobile overlay */}
      {isMobileOpen && (
        <div
          className="lg:hidden fixed inset-0 z-40"
          style={{ backgroundColor: "var(--color-bg-overlay)" }}
          onClick={() => setIsMobileOpen(false)}
        />
      )}

      <div
        className={`
          h-screen fixed left-0 top-0 transition-all duration-300 flex flex-col
          ${isCollapsed ? "lg:w-20" : "lg:w-64"}
          w-64
          ${isMobileOpen ? "translate-x-0 z-50" : "-translate-x-full z-50"}
          lg:translate-x-0 lg:z-50
        `}
        style={{
          backgroundColor: "var(--color-bg-primarySideBarHeader)",
          borderRight: "1px solid var(--color-border-primary)",
          color: "var(--color-text-primary)",
        }}
      >
        {/* ── Header ── */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "var(--spacing-lg) var(--spacing-lg) var(--spacing-md)",
            borderBottom: "1px solid var(--color-border-primary)",
          }}
        >
          {!isCollapsed && (
            <Link to="/dashboard" onClick={handleLinkClick} style={{ textDecoration: "none" }}>
              <Typography variant="title" sx={{ color: "var(--color-primary)" }}>
                FactorAlpha
              </Typography>
            </Link>
          )}

          {/* Mobile close */}
          {isMobileOpen && window.innerWidth < 768 && (
            <button
              onClick={() => setIsMobileOpen(false)}
              style={{
                ...itemBase,
                width: "auto",
                padding: "var(--spacing-sm)",
                marginLeft: "auto",
                color: "var(--color-primary)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              <X size={20} />
            </button>
          )}

          {/* Desktop collapse toggle */}
          {!isMobileOpen && (
            <button
              onClick={() => setIsCollapsed(!isCollapsed)}
              className="hidden lg:block"
              style={{
                ...itemBase,
                width: "auto",
                padding: "var(--spacing-sm)",
                marginLeft: "auto",
                color: "var(--color-primary)",
              }}
              onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
            >
              {isCollapsed ? <ChevronRight size={20} /> : <ChevronLeft size={20} />}
            </button>
          )}
        </div>

        {/* ── Menu ── */}
        <div
          className="flex-1 overflow-y-auto sidebar-scroll"
          style={{ padding: "var(--spacing-md) 0" }}
        >
          {menuSections.map((section, sectionIndex) => (
            <div key={sectionIndex} style={{ marginBottom: "var(--spacing-lg)" }}>

              {/* Section title */}
              {section.title && !isCollapsed && (
                <div style={{ padding: "0 var(--spacing-lg)", marginBottom: "var(--spacing-xs)" }}>
                  <Typography
                    variant="paragraph"
                    sx={{
                      fontSize: "var(--font-size-sm)",
                      fontWeight: "var(--font-weight-semibold)",
                      color: "var(--color-text-muted)",
                      textTransform: "uppercase",
                      letterSpacing: "0.08em",
                    }}
                  >
                    {section.title}
                  </Typography>
                </div>
              )}

              <nav style={{ display: "flex", flexDirection: "column", gap: "2px", padding: "0 var(--spacing-sm)" }}>
                {section.items.map((item) => {
                  const Icon = item.icon;

                  /* ── CREATE PORTFOLIO ACTION ── */
                  if (item.action === "createPortfolio") {
                    return (
                      <SideTooltip key={item.name} label={item.name} collapsed={isCollapsed}>
                        <button
                          onClick={() => {
                            handleLinkClick();
                            navigate("/allPortfolio", { state: { openCreatePortfolio: true } });
                          }}
                          style={{ ...itemBase, ...itemInactive }}
                          onMouseEnter={onHoverIn}
                          onMouseLeave={(e) => onHoverOut(e, false)}
                        >
                          <Icon size={20} style={{ flexShrink: 0 }} />
                          {!isCollapsed && (
                            <Typography
                              variant="paragraph"
                              sx={{
                                fontSize: "var(--font-size-base)",
                                fontWeight: "var(--font-weight-medium)",
                                color: "inherit",
                              }}
                            >
                              {item.name}
                            </Typography>
                          )}
                        </button>
                      </SideTooltip>
                    );
                  }

                  /* ── EXPANDABLE PORTFOLIOS ── */
                  if (item.expandable) {
                    const isOpen = openMenu === item.name;
                    const active = isActive(item.path) || isOnPortfolioDetail;

                    return (
                      <div key={item.name}>
                        <SideTooltip label={item.name} collapsed={isCollapsed}>
                          <div
                            style={{ ...itemBase, ...(active ? itemActive : itemInactive) }}
                            onMouseEnter={(e) => { if (!active) onHoverIn(e); }}
                            onMouseLeave={(e) => onHoverOut(e, active)}
                          >
                            <Link
                              to={item.path}
                              onClick={handleLinkClick}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "var(--spacing-md)",
                                flex: 1,
                                minWidth: 0,
                                textDecoration: "none",
                                color: "inherit",
                              }}
                            >
                              <Icon size={20} style={{ flexShrink: 0 }} />
                              {!isCollapsed && (
                                <Typography
                                  variant="paragraph"
                                  sx={{
                                    fontSize: "var(--font-size-base)",
                                    fontWeight: "var(--font-weight-medium)",
                                    color: "inherit",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                              )}
                            </Link>

                            {!isCollapsed && (
                              <button
                                onClick={(e) => { e.stopPropagation(); handlePortfolioToggle(); }}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "var(--spacing-sm)",
                                  borderRadius: "var(--radius-sm)",
                                  color: "inherit",
                                  flexShrink: 0,
                                  transition: "background-color var(--transition-fast)",
                                }}
                                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--color-surface-active)")}
                                onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = "transparent")}
                              >
                                <ChevronDown
                                  size={16}
                                  style={{
                                    transition: "transform 200ms ease",
                                    transform: isOpen ? "rotate(180deg)" : "rotate(0deg)",
                                  }}
                                />
                              </button>
                            )}
                          </div>
                        </SideTooltip>

                        {/* Portfolio sub-items — only visible when expanded */}
                        {isOpen && !isCollapsed && (
                          <div style={{ marginLeft: "2.25rem", marginTop: "var(--spacing-xs)", display: "flex", flexDirection: "column", gap: "2px" }}>
                            {portfoliosLoading ? (
                              <div style={{ display: "flex", alignItems: "center", gap: "var(--spacing-sm)", padding: "var(--spacing-sm) var(--spacing-md)" }}>
                                <Loader2 size={14} className="animate-spin" style={{ color: "var(--color-text-muted)" }} />
                                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", color: "var(--color-text-muted)" }}>
                                  Loading...
                                </Typography>
                              </div>
                            ) : portfolios.length === 0 ? (
                              <div style={{ padding: "var(--spacing-base) var(--spacing-md)" }}>
                                <Typography variant="paragraph" sx={{ fontSize: "var(--font-size-base)", color: "var(--color-text-muted)" }}>
                                  No portfolios yet
                                </Typography>
                              </div>
                            ) : (
                              portfolios.map((portfolio) => {
                                const portfolioPath = `/portfolio/${portfolio.id}`;
                                const isPortfolioActive =
                                  location.pathname === portfolioPath ||
                                  location.pathname.startsWith(`${portfolioPath}/`);

                                return (
                                  <Link
                                    key={portfolio.id}
                                    to={portfolioPath}
                                    onClick={handleLinkClick}
                                    title={portfolio.name}
                                    style={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: "var(--spacing-sm)",
                                      padding: "var(--spacing-sm) var(--spacing-md)",
                                      borderRadius: "var(--radius-md)",
                                      textDecoration: "none",
                                      transition: "all var(--transition-fast)",
                                      color: isPortfolioActive ? "var(--color-text-primary)" : "var(--color-text-muted)",
                                      backgroundColor: isPortfolioActive ? "var(--color-surface-hover)" : "transparent",
                                      borderLeft: isPortfolioActive ? "2px solid var(--color-primary)" : "none",
                                      paddingLeft: isPortfolioActive ? "calc(var(--spacing-md) - 2px)" : "var(--spacing-md)",
                                      overflow: "hidden",
                                    }}
                                    onMouseEnter={(e) => { if (!isPortfolioActive) { e.currentTarget.style.color = "var(--color-text-primary)"; e.currentTarget.style.backgroundColor = "var(--color-surface-hover)"; } }}
                                    onMouseLeave={(e) => { if (!isPortfolioActive) { e.currentTarget.style.color = "var(--color-text-muted)"; e.currentTarget.style.backgroundColor = "transparent"; } }}
                                  >
                                    <Typography
                                      variant="paragraph"
                                      sx={{
                                        fontSize: "var(--font-size-base)",
                                        color: "inherit",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        whiteSpace: "nowrap",
                                        flex: 1,
                                      }}
                                    >
                                      {portfolio.name}
                                    </Typography>

                                    {portfolio.holdings_count !== undefined && (
                                      <span
                                        style={{
                                          marginLeft: "auto",
                                          fontSize: "var(--font-size-base)",
                                          fontWeight: "var(--font-weight-medium)",
                                          padding: "2px 6px",
                                          borderRadius: "var(--radius-full)",
                                          flexShrink: 0,
                                          backgroundColor: isPortfolioActive ? "var(--color-primary-lighter)" : "var(--color-surface-active)",
                                          color: isPortfolioActive ? "var(--color-primary)" : "var(--color-text-muted)",
                                        }}
                                      >
                                        {portfolio.holdings_count}
                                      </span>
                                    )}
                                  </Link>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  }

                  /* ── SUB MENU ── */
                  if (item.subMenu) {
                    const isOpen =
                      openMenu === item.name ||
                      item.subMenu.some((sub) => location.pathname === sub.path);
                    const isSubActive = item.subMenu.some((sub) => location.pathname === sub.path);

                    return (
                      <div key={item.name}>
                        <SideTooltip label={item.name} collapsed={isCollapsed}>
                          <button
                            onClick={() => setOpenMenu(isOpen ? null : item.name)}
                            style={{ ...itemBase, ...(isSubActive ? itemActive : itemInactive) }}
                            onMouseEnter={(e) => { if (!isSubActive) onHoverIn(e); }}
                            onMouseLeave={(e) => onHoverOut(e, isSubActive)}
                          >
                            <item.icon size={20} style={{ flexShrink: 0 }} />
                            {!isCollapsed && (
                              <>
                                <Typography
                                  variant="paragraph"
                                  sx={{
                                    fontSize: "var(--font-size-base)",
                                    fontWeight: "var(--font-weight-medium)",
                                    color: "inherit",
                                    flex: 1,
                                    textAlign: "left",
                                  }}
                                >
                                  {item.name}
                                </Typography>
                                <ChevronRight
                                  size={16}
                                  style={{
                                    flexShrink: 0,
                                    transition: "transform var(--transition-fast)",
                                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                                  }}
                                />
                              </>
                            )}
                          </button>
                        </SideTooltip>

                        {isOpen && !isCollapsed && (
                          <div style={{ marginLeft: "2.25rem", marginTop: "var(--spacing-xs)", display: "flex", flexDirection: "column", gap: "2px" }}>
                            {item.subMenu.map((sub) => {
                              const subActive = location.pathname === sub.path;
                              return (
                                <Link
                                  key={sub.path}
                                  to={sub.path}
                                  onClick={handleLinkClick}
                                  style={{
                                    display: "block",
                                    padding: "var(--spacing-sm) var(--spacing-md)",
                                    borderRadius: "var(--radius-md)",
                                    textDecoration: "none",
                                    transition: "all var(--transition-fast)",
                                    color: subActive ? "var(--color-text-primary)" : "var(--color-text-muted)",
                                    backgroundColor: subActive ? "var(--color-surface-hover)" : "transparent",
                                  }}
                                  onMouseEnter={(e) => { if (!subActive) { e.currentTarget.style.color = "var(--color-text-primary)"; e.currentTarget.style.backgroundColor = "var(--color-surface-hover)"; } }}
                                  onMouseLeave={(e) => { if (!subActive) { e.currentTarget.style.color = "var(--color-text-muted)"; e.currentTarget.style.backgroundColor = "transparent"; } }}
                                >
                                  <Typography
                                    variant="paragraph"
                                    sx={{
                                      fontSize: "var(--font-size-base)",
                                      fontWeight: subActive ? "var(--font-weight-medium)" : "var(--font-weight-normal)",
                                      color: "inherit",
                                    }}
                                  >
                                    {sub.name}
                                  </Typography>
                                </Link>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  }

                  /* ── NORMAL LINKS ── */
                  const active = item.name === "Newsletters" ? isNewsletterActive() : isActive(item.path);

                  return (
                    <SideTooltip key={item.path} label={item.name} collapsed={isCollapsed}>
                      <Link
                        to={item.path}
                        onClick={handleLinkClick}
                        style={{
                          ...itemBase,
                          alignItems: "center",
                          ...(active ? itemActive : itemInactive),
                        }}
                        onMouseEnter={(e) => { if (!active) onHoverIn(e); }}
                        onMouseLeave={(e) => onHoverOut(e, active)}
                      >
                        <Icon size={20} style={{ flexShrink: 0 }} />
                        {!isCollapsed && (
                          <Typography
                            variant="paragraph"
                            sx={{
                              fontSize: "var(--font-size-base)",
                              fontWeight: "var(--font-weight-medium)",
                              color: "inherit",
                              lineHeight: 1.2,
                              whiteSpace: "normal",
                              overflow: "visible",
                              textOverflow: "unset",
                              flex: 1,
                              minWidth: 0,
                            }}
                          >
                            {item.name}
                          </Typography>
                        )}
                      </Link>
                    </SideTooltip>
                  );
                })}
              </nav>

              {sectionIndex < menuSections.length - 1 && (
                <div style={{ borderTop: "1px solid var(--color-border-primary)", marginTop: "var(--spacing-lg)" }} />
              )}
            </div>
          ))}
        </div>

        {/* ── Footer ── */}
        <div style={{ borderTop: "1px solid var(--color-border-primary)", display: "flex", padding: "var(--spacing-md)", justifyContent: "center" }}>
          {!isCollapsed ? (
            <Typography
              variant="paragraph"
              sx={{ fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)", textAlign: "center" }}
            >
              © 2026 FactorAlpha
            </Typography>
          ) : (
            <div className="hidden lg:flex justify-center">
              <div style={{ width: "8px", height: "8px", borderRadius: "var(--radius-full)", backgroundColor: "var(--color-primary)" }} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Sidebar;